import React from "react";
import {observer} from "mobx-react-lite";
import {
    Autocomplete,
    Button,
    Checkbox,
    FormControl,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import {Add, Apartment, CalendarMonth, Category as CategoryIcon, Clear, Search} from "@mui/icons-material";
import {generatePath, useNavigate} from "react-router";
import {sitRoutes} from "~/sit/routing/sitRoutes.ts";
import {useSitStores} from "~/stores";
import type {Category, Company} from "~/sit/ApiTypes.ts";
import {category_readable_str, ComplexityTierValues, DOIs} from "~/sit/ApiTypes.ts";
import {complexityTierLabel, sitFieldLabel, stageGateLabel} from "~/sit/sitLabels";
import {sitCountTags} from "~/sit/components/design/SitDesignAtoms.tsx";

// Prepend a leading icon to an Autocomplete's input, keeping its own adornment (tags).
const withIcon = (icon: React.ReactNode, params: React.ComponentProps<typeof TextField>['InputProps']) => ({
    ...params,
    startAdornment: <><InputAdornment position="start">{icon}</InputAdornment>{params?.startAdornment}</>,
});

export const InitiativeFilters: React.FC = observer(() => {
    const {initiativeListStore: store, ccStore} = useSitStores();
    const navigate = useNavigate();
    const [exporting, setExporting] = React.useState(false);
    const yearOptions = Array.from({length: 6}, (_v, i) => new Date().getFullYear() - 2 + i);

    // Download the (currently filtered) initiative list as an Efficiency-Map .xlsx.
    const onExport = () => {
        setExporting(true);
        store.exportInitiatives().then((resp) => {
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'mapa_de_eficiencias.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        }).finally(() => setExporting(false));
    };

    return (
        <div className="initiative-filters-component sit-filterbar">
            <TextField
                className="sit-search-field"
                size="small"
                placeholder="Search initiatives"
                value={store.search}
                onChange={e => store.setSearch(e.target.value)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment>,
                    endAdornment: store.search
                        ? <InputAdornment position="end">
                            <IconButton size="small" aria-label="clear search" onClick={() => store.setSearch('')}>
                                <Clear fontSize="small"/>
                            </IconButton>
                        </InputAdornment>
                        : undefined,
                }}/>

            <FormControl size="small" className="sitf sit-filter-select">
                <span className="sitf-label">View</span>
                <Select value={store.my_initiatives}
                        onChange={e => {
                            if (typeof e.target.value === 'number') store.setMyInitiatives(e.target.value);
                        }}>
                    <MenuItem value={0}>All initiatives</MenuItem>
                    <MenuItem value={1}>My initiatives</MenuItem>
                </Select>
            </FormControl>

            <label className="sitf sit-filter-auto">
                <span className="sitf-label">Category</span>
                <Autocomplete
                    multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                    size="small" limitTags={1} renderTags={sitCountTags}
                    options={ccStore.categoryRM.result ?? ([] as Category[])}
                    getOptionLabel={(option) => category_readable_str(option)}
                    value={store.categories}
                    renderInput={(params) =>
                        <TextField {...params} placeholder={store.categories.length ? 'Search' : 'All'}
                                   InputProps={withIcon(<CategoryIcon fontSize="small"/>, params.InputProps)}/>}
                    renderOption={(props, option) =>
                        <li {...props} key={option.id}>
                            <Checkbox checked={store.categories.find((c) => c.id === option.id) !== undefined}/>
                            {category_readable_str(option)}
                        </li>}
                    onChange={(_e, value) => store.setCategories(value)}/>
            </label>

            <label className="sitf sit-filter-auto">
                <span className="sitf-label">Company</span>
                <Autocomplete
                    multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                    size="small" limitTags={1} renderTags={sitCountTags}
                    options={ccStore.companyRM.result ?? ([] as Company[])}
                    getOptionLabel={(option) => option.title}
                    value={store.companies}
                    renderInput={(params) =>
                        <TextField {...params} placeholder={store.companies.length ? 'Search' : 'All'}
                                   InputProps={withIcon(<Apartment fontSize="small"/>, params.InputProps)}/>}
                    renderOption={(props, option) =>
                        <li {...props} key={option.id}>
                            <Checkbox checked={store.companies.find((c) => c.id === option.id) !== undefined}/>
                            {option.title}
                        </li>}
                    onChange={(_e, value) => store.setCompanies(value)}/>
            </label>

            <FormControl size="small" className="sitf sit-filter-select">
                <span className="sitf-label">Stage</span>
                <Autocomplete
                    multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                    size="small" limitTags={1} renderTags={sitCountTags}
                    options={DOIs}
                    getOptionLabel={(option) => stageGateLabel(option)}
                    value={store.dois}
                    renderInput={(params) =>
                        <TextField {...params} placeholder={store.dois.length ? 'Search' : 'All'}/>}
                    renderOption={(props, option) =>
                        <li {...props} key={option} style={{whiteSpace: 'nowrap'}}>
                            <Checkbox checked={store.dois.includes(option)}/>
                            {stageGateLabel(option)}
                        </li>}
                    onChange={(_e, value) => store.setDois(value)}/>
            </FormControl>

            <FormControl size="small" className="sitf sit-filter-select">
                <span className="sitf-label">{sitFieldLabel('complexityTier')}</span>
                <Autocomplete
                    multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                    size="small" limitTags={1} renderTags={sitCountTags}
                    options={ComplexityTierValues}
                    getOptionLabel={(option) => complexityTierLabel(option)}
                    value={store.complexity_tiers}
                    renderInput={(params) =>
                        <TextField {...params} placeholder={store.complexity_tiers.length ? 'Search' : 'All'}/>}
                    renderOption={(props, option) =>
                        <li {...props} key={option} style={{whiteSpace: 'nowrap'}}>
                            <Checkbox checked={store.complexity_tiers.includes(option)}/>
                            {complexityTierLabel(option)}
                        </li>}
                    onChange={(_e, value) => store.setComplexityTiers(value)}/>
            </FormControl>

            {DOIs.map((doi) =>
                <FormControl key={doi} size="small" className="sitf sit-filter-select sit-filter-year">
                    <span className="sitf-label">{sitFieldLabel('year')} ({stageGateLabel(doi)})</span>
                    <Autocomplete
                        multiple fullWidth selectOnFocus clearOnBlur handleHomeEndKeys disableCloseOnSelect
                        size="small" limitTags={1} renderTags={sitCountTags}
                        options={yearOptions}
                        getOptionLabel={(option) => String(option)}
                        value={store.yearsByDoi[doi]}
                        renderInput={(params) =>
                            <TextField {...params} placeholder={store.yearsByDoi[doi].length ? 'Search' : 'All'}
                                       InputProps={withIcon(<CalendarMonth fontSize="small"/>, params.InputProps)}/>}
                        renderOption={(props, option) =>
                            <li {...props} key={option} style={{whiteSpace: 'nowrap'}}>
                                <Checkbox checked={store.yearsByDoi[doi].includes(option)}/>
                                {option}
                            </li>}
                        onChange={(_e, value) => store.setYearsForDoi(doi, value)}/>
                </FormControl>)}

            <span className="sit-filterbar-spacer"/>

            {/*<Button variant="outlined" className="sit-export-btn" disabled={exporting}*/}
            {/*        startIcon={exporting ? <CircularProgress size={14}/> : <FileDownload/>}*/}
            {/*        onClick={onExport}>*/}
            {/*    Export*/}
            {/*</Button>*/}

            <Button variant="contained" className="sit-add-btn" startIcon={<Add/>}
                    onClick={() => navigate(generatePath(sitRoutes.sit_initiative, {initiativeId: 'new'}))}>
                Add new initiative
            </Button>
        </div>
    );
});
